import { Plus, Trash2, GripVertical } from 'lucide-react'
import { RichTextEditor } from './RichTextEditor'

// Generic item editor props
interface ItemEditorProps<T> {
  items: T[]
  onChange: (items: T[]) => void
  renderItem: (item: T, index: number, onChange: (item: T) => void) => React.ReactNode
  createNew: () => T
  itemLabel?: string
}

function ItemListEditor<T>({ items, onChange, renderItem, createNew, itemLabel = 'Item' }: ItemEditorProps<T>) {
  const addItem = () => onChange([...items, createNew()])
  const removeItem = (index: number) => onChange(items.filter((_, i) => i !== index))
  const updateItem = (index: number, item: T) => {
    const newItems = [...items]
    newItems[index] = item
    onChange(newItems)
  }

  return (
    <div className="space-y-3">
      {items.map((item, index) => (
        <div key={index} className="flex gap-2 p-3 border border-border rounded-lg bg-muted/20">
          <div className="text-muted-foreground cursor-grab">
            <GripVertical className="h-5 w-5" />
          </div>
          <div className="flex-1 space-y-2">
            {renderItem(item, index, (newItem) => updateItem(index, newItem))}
          </div>
          <button
            type="button"
            onClick={() => removeItem(index)}
            className="p-1 h-fit hover:bg-destructive/10 text-destructive rounded"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      ))}
      <button
        type="button"
        onClick={addItem}
        className="w-full py-2 border-2 border-dashed border-border rounded-lg text-muted-foreground hover:border-primary hover:text-primary transition-colors flex items-center justify-center gap-2"
      >
        <Plus className="h-4 w-4" />
        Add {itemLabel}
      </button>
    </div>
  )
}

// Features Section Editor
interface Feature {
  title: string
  description: string
  icon?: string
}

interface FeaturesSectionEditorProps {
  content: { features: Feature[] }
  onChange: (content: { features: Feature[] }) => void
}

export function FeaturesSectionEditor({ content, onChange }: FeaturesSectionEditorProps) {
  return (
    <ItemListEditor
      items={content.features || []}
      onChange={(features) => onChange({ ...content, features })}
      createNew={() => ({ title: 'New Feature', description: 'Feature description' })}
      itemLabel="Feature"
      renderItem={(item, _, onItemChange) => (
        <>
          <input
            value={item.title}
            onChange={(e) => onItemChange({ ...item, title: e.target.value })}
            placeholder="Feature title"
            className="input input-sm w-full"
          />
          <textarea
            value={item.description}
            onChange={(e) => onItemChange({ ...item, description: e.target.value })}
            placeholder="Feature description"
            className="input input-sm w-full min-h-[60px]"
          />
        </>
      )}
    />
  )
}

// Benefits Section Editor
interface Benefit {
  title: string
  description: string
}

interface BenefitsSectionEditorProps {
  content: { benefits: Benefit[] }
  onChange: (content: { benefits: Benefit[] }) => void
}

export function BenefitsSectionEditor({ content, onChange }: BenefitsSectionEditorProps) {
  return (
    <ItemListEditor
      items={content.benefits || []}
      onChange={(benefits) => onChange({ ...content, benefits })}
      createNew={() => ({ title: 'New Benefit', description: 'Benefit description' })}
      itemLabel="Benefit"
      renderItem={(item, _, onItemChange) => (
        <>
          <input
            value={item.title}
            onChange={(e) => onItemChange({ ...item, title: e.target.value })}
            placeholder="Benefit title"
            className="input input-sm w-full"
          />
          <textarea
            value={item.description}
            onChange={(e) => onItemChange({ ...item, description: e.target.value })}
            placeholder="Benefit description"
            className="input input-sm w-full min-h-[60px]"
          />
        </>
      )}
    />
  )
}

// Testimonials Section Editor
interface Testimonial {
  quote: string
  author: string
}

interface TestimonialsSectionEditorProps {
  content: { testimonials: Testimonial[] }
  onChange: (content: { testimonials: Testimonial[] }) => void
}

export function TestimonialsSectionEditor({ content, onChange }: TestimonialsSectionEditorProps) {
  return (
    <ItemListEditor
      items={content.testimonials || []}
      onChange={(testimonials) => onChange({ ...content, testimonials })}
      createNew={() => ({ quote: 'Great experience!', author: 'Customer Name' })}
      itemLabel="Testimonial"
      renderItem={(item, _, onItemChange) => (
        <>
          <textarea
            value={item.quote}
            onChange={(e) => onItemChange({ ...item, quote: e.target.value })}
            placeholder="Testimonial quote"
            className="input input-sm w-full min-h-[80px]"
          />
          <input
            value={item.author}
            onChange={(e) => onItemChange({ ...item, author: e.target.value })}
            placeholder="Author name"
            className="input input-sm w-full"
          />
        </>
      )}
    />
  )
}

// FAQ Section Editor
interface FAQItem {
  question: string
  answer: string
}

interface FAQSectionEditorProps {
  content: { items: FAQItem[] }
  onChange: (content: { items: FAQItem[] }) => void
}

export function FAQSectionEditor({ content, onChange }: FAQSectionEditorProps) {
  return (
    <ItemListEditor
      items={content.items || []}
      onChange={(items) => onChange({ ...content, items })}
      createNew={() => ({ question: 'New question?', answer: 'Answer here...' })}
      itemLabel="FAQ"
      renderItem={(item, _, onItemChange) => (
        <>
          <input
            value={item.question}
            onChange={(e) => onItemChange({ ...item, question: e.target.value })}
            placeholder="Question"
            className="input input-sm w-full font-medium"
          />
          <textarea
            value={item.answer}
            onChange={(e) => onItemChange({ ...item, answer: e.target.value })}
            placeholder="Answer"
            className="input input-sm w-full min-h-[80px]"
          />
        </>
      )}
    />
  )
}

// Pricing Section Editor
interface PricingSectionEditorProps {
  content: {
    price: string
    features: string[]
    ctaText: string
    ctaUrl: string
    period?: string
  }
  onChange: (content: PricingSectionEditorProps['content']) => void
}

export function PricingSectionEditor({ content, onChange }: PricingSectionEditorProps) {
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="label mb-1 block">Price</label>
          <input
            value={content.price}
            onChange={(e) => onChange({ ...content, price: e.target.value })}
            placeholder="$29/month"
            className="input"
          />
        </div>
        <div>
          <label className="label mb-1 block">Period</label>
          <input
            value={content.period || ''}
            onChange={(e) => onChange({ ...content, period: e.target.value })}
            placeholder="per month"
            className="input"
          />
        </div>
      </div>

      <div>
        <label className="label mb-1 block">Features</label>
        <ItemListEditor
          items={content.features || []}
          onChange={(features) => onChange({ ...content, features })}
          createNew={() => 'New feature'}
          itemLabel="Feature"
          renderItem={(item, _, onItemChange) => (
            <input
              value={item}
              onChange={(e) => onItemChange(e.target.value)}
              placeholder="Feature"
              className="input input-sm w-full"
            />
          )}
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="label mb-1 block">Button Text</label>
          <input
            value={content.ctaText}
            onChange={(e) => onChange({ ...content, ctaText: e.target.value })}
            placeholder="Get Started"
            className="input"
          />
        </div>
        <div>
          <label className="label mb-1 block">Button URL</label>
          <input
            value={content.ctaUrl}
            onChange={(e) => onChange({ ...content, ctaUrl: e.target.value })}
            placeholder="#signup"
            className="input"
          />
        </div>
      </div>
    </div>
  )
}

// CTA Section Editor
interface CTASectionEditorProps {
  content: {
    headline?: string
    description: string
    ctaText?: string
    ctaUrl?: string
  }
  onChange: (content: CTASectionEditorProps['content']) => void
}

export function CTASectionEditor({ content, onChange }: CTASectionEditorProps) {
  return (
    <div className="space-y-4">
      <div>
        <label className="label mb-1 block">Headline (optional)</label>
        <input
          value={content.headline || ''}
          onChange={(e) => onChange({ ...content, headline: e.target.value })}
          placeholder="Ready to get started?"
          className="input"
        />
      </div>
      <div>
        <label className="label mb-1 block">Description</label>
        <RichTextEditor
          content={content.description}
          onChange={(description) => onChange({ ...content, description })}
          placeholder="Your call to action message..."
          minHeight="80px"
        />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="label mb-1 block">Button Text</label>
          <input
            value={content.ctaText || ''}
            onChange={(e) => onChange({ ...content, ctaText: e.target.value })}
            placeholder="Contact Us"
            className="input"
          />
        </div>
        <div>
          <label className="label mb-1 block">Button URL</label>
          <input
            value={content.ctaUrl || ''}
            onChange={(e) => onChange({ ...content, ctaUrl: e.target.value })}
            placeholder="#contact"
            className="input"
          />
        </div>
      </div>
    </div>
  )
}

// Content/Text Section Editor
interface ContentSectionEditorProps {
  content: { text: string }
  onChange: (content: { text: string }) => void
}

export function ContentSectionEditor({ content, onChange }: ContentSectionEditorProps) {
  return (
    <RichTextEditor
      content={content.text || ''}
      onChange={(text) => onChange({ text })}
      placeholder="Write your content here..."
      minHeight="200px"
    />
  )
}

// Articles Section Editor
interface Article {
  title: string
  excerpt: string
  category: string
  date: string
  readTime: string
  url?: string
}

interface ArticlesSectionEditorProps {
  content: { articles: Article[] }
  onChange: (content: { articles: Article[] }) => void
}

export function ArticlesSectionEditor({ content, onChange }: ArticlesSectionEditorProps) {
  return (
    <ItemListEditor
      items={content.articles || []}
      onChange={(articles) => onChange({ ...content, articles })}
      createNew={() => ({
        title: 'New Article',
        excerpt: 'Article excerpt...',
        category: 'News',
        date: new Date().toISOString().split('T')[0],
        readTime: '3 min read',
      })}
      itemLabel="Article"
      renderItem={(item, _, onItemChange) => (
        <>
          <input
            value={item.title}
            onChange={(e) => onItemChange({ ...item, title: e.target.value })}
            placeholder="Article title"
            className="input input-sm w-full font-medium"
          />
          <textarea
            value={item.excerpt}
            onChange={(e) => onItemChange({ ...item, excerpt: e.target.value })}
            placeholder="Article excerpt"
            className="input input-sm w-full min-h-[60px]"
          />
          <div className="grid grid-cols-3 gap-2">
            <input
              value={item.category}
              onChange={(e) => onItemChange({ ...item, category: e.target.value })}
              placeholder="Category"
              className="input input-sm"
            />
            <input
              type="date"
              value={item.date}
              onChange={(e) => onItemChange({ ...item, date: e.target.value })}
              className="input input-sm"
            />
            <input
              value={item.readTime}
              onChange={(e) => onItemChange({ ...item, readTime: e.target.value })}
              placeholder="5 min read"
              className="input input-sm"
            />
          </div>
        </>
      )}
    />
  )
}

// Categories Section Editor
interface Category {
  name: string
  count: number
  icon?: string
}

interface CategoriesSectionEditorProps {
  content: { categories: Category[] }
  onChange: (content: { categories: Category[] }) => void
}

export function CategoriesSectionEditor({ content, onChange }: CategoriesSectionEditorProps) {
  return (
    <ItemListEditor
      items={content.categories || []}
      onChange={(categories) => onChange({ ...content, categories })}
      createNew={() => ({ name: 'New Category', count: 0, icon: '📁' })}
      itemLabel="Category"
      renderItem={(item, _, onItemChange) => (
        <div className="grid grid-cols-[auto_1fr_80px] gap-2 items-center">
          <input
            value={item.icon || ''}
            onChange={(e) => onItemChange({ ...item, icon: e.target.value })}
            placeholder="📁"
            className="input input-sm w-12 text-center"
          />
          <input
            value={item.name}
            onChange={(e) => onItemChange({ ...item, name: e.target.value })}
            placeholder="Category name"
            className="input input-sm"
          />
          <input
            type="number"
            value={item.count}
            onChange={(e) => onItemChange({ ...item, count: parseInt(e.target.value) || 0 })}
            placeholder="Count"
            className="input input-sm"
          />
        </div>
      )}
    />
  )
}

// Newsletter Section Editor
interface NewsletterSectionEditorProps {
  content: {
    headline?: string
    description: string
    placeholder: string
    buttonText: string
  }
  onChange: (content: NewsletterSectionEditorProps['content']) => void
}

export function NewsletterSectionEditor({ content, onChange }: NewsletterSectionEditorProps) {
  return (
    <div className="space-y-4">
      <div>
        <label className="label mb-1 block">Headline (optional)</label>
        <input
          value={content.headline || ''}
          onChange={(e) => onChange({ ...content, headline: e.target.value })}
          placeholder="Stay in the loop"
          className="input"
        />
      </div>
      <div>
        <label className="label mb-1 block">Description</label>
        <textarea
          value={content.description}
          onChange={(e) => onChange({ ...content, description: e.target.value })}
          placeholder="Subscribe to get updates..."
          className="input min-h-[80px]"
        />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="label mb-1 block">Input Placeholder</label>
          <input
            value={content.placeholder}
            onChange={(e) => onChange({ ...content, placeholder: e.target.value })}
            placeholder="Enter your email"
            className="input"
          />
        </div>
        <div>
          <label className="label mb-1 block">Button Text</label>
          <input
            value={content.buttonText}
            onChange={(e) => onChange({ ...content, buttonText: e.target.value })}
            placeholder="Subscribe"
            className="input"
          />
        </div>
      </div>
    </div>
  )
}

// Export a map for easy lookup
export const SECTION_EDITORS: Record<string, React.ComponentType<{ content: any; onChange: (content: any) => void }>> = {
  features: FeaturesSectionEditor,
  benefits: BenefitsSectionEditor,
  testimonials: TestimonialsSectionEditor,
  faq: FAQSectionEditor,
  pricing: PricingSectionEditor,
  cta: CTASectionEditor,
  content: ContentSectionEditor,
  articles: ArticlesSectionEditor,
  categories: CategoriesSectionEditor,
  newsletter: NewsletterSectionEditor,
}
